// Which store am I acting for, and what am I allowed to do there?
// ───────────────────────────────────────────────────────────────
// Owners are their store: businessId === uid. Seats are employees with
// their own uid, and carry { businessId, seatRole } as custom claims.
// Business pages should read businessId from here rather than user.uid,
// otherwise a seat would query its own uid and see an empty store.

import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../config/firebase';
import { seatCan, type SeatRole, type SeatPermission } from '../types/business';

export interface BusinessContext {
  /** The store to query. Null while loading or when the user has no store. */
  businessId: string | null;
  isOwner: boolean;
  isSeat: boolean;
  seatRole: SeatRole | null;
  loading: boolean;
  /** Owners get everything; seats get their preset. */
  can: (permission: SeatPermission) => boolean;
  /** Re-reads claims after a role change without a full sign-out. */
  refresh: () => Promise<void>;
}

interface ClaimState {
  businessId: string | null;
  seatRole: SeatRole | null;
}

async function readClaims(user: User | null, forceRefresh = false): Promise<ClaimState> {
  if (!user) return { businessId: null, seatRole: null };

  const token = await user.getIdTokenResult(forceRefresh);
  const businessId = typeof token.claims.businessId === 'string' ? token.claims.businessId : null;
  const seatRole =
    token.claims.seatRole === 'manager' || token.claims.seatRole === 'staff'
      ? (token.claims.seatRole as SeatRole)
      : null;

  return { businessId, seatRole };
}

export function useBusinessContext(): BusinessContext {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [claims, setClaims] = useState<ClaimState>({ businessId: null, seatRole: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (cancelled) return;
      setUser(nextUser);

      try {
        const next = await readClaims(nextUser);
        if (!cancelled) setClaims(next);
      } catch (err) {
        console.error('Error reading business claims:', err);
        if (!cancelled) setClaims({ businessId: null, seatRole: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const refresh = useCallback(async () => {
    setClaims(await readClaims(auth.currentUser, true));
  }, []);

  const isSeat = !!claims.businessId && !!claims.seatRole;
  const businessId = isSeat ? claims.businessId : user?.uid ?? null;

  const can = useCallback(
    (permission: SeatPermission) => (isSeat ? seatCan(claims.seatRole, permission) : !!user),
    [isSeat, claims.seatRole, user]
  );

  return {
    businessId,
    isOwner: !!user && !isSeat,
    isSeat,
    seatRole: claims.seatRole,
    loading,
    can,
    refresh,
  };
}
